import React from 'react';
import { Helmet } from 'react-helmet-async';
import Reveal from '../components/marketing/Reveal';

const PrivacyPage = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | EdAI Systems</title>
        <meta
          name="description"
          content="EdAI Systems privacy policy. How we collect, use, and protect your information."
        />
        <meta property="og:image" content="https://edaisystems.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://edaisystems.com/og-image.png" />
      </Helmet>

      <div className="min-h-screen pt-24 pb-16">
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <Reveal>
              <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
                Privacy Policy
              </h1>
              <p className="text-text-muted mb-12">
                Effective: March 2026
              </p>
            </Reveal>

            <Reveal delay={100}>
              <div className="prose prose-invert max-w-none">
                {/* Section 1 */}
                <section className="mb-12">
                  <h2 className="text-2xl font-bold text-text-primary mb-4">
                    Information We Collect
                  </h2>
                  <div className="text-text-secondary space-y-4">
                    <p>
                      <strong className="text-text-primary">Information you provide:</strong> When you request a demo, sign up for updates, or contact us, we collect information such as your name, email address, organization, and role.
                    </p>
                    <p>
                      <strong className="text-text-primary">Usage data:</strong> We collect information about how you interact with our website, including pages visited, features explored, and session duration.
                    </p>
                    <p>
                      <strong className="text-text-primary">Device information:</strong> We automatically collect certain technical information, including browser type, operating system, and IP address.
                    </p>
                  </div>
                </section>

                {/* Section 2 */}
                <section className="mb-12">
                  <h2 className="text-2xl font-bold text-text-primary mb-4">
                    How We Use Your Information
                  </h2>
                  <ul className="text-text-secondary space-y-3 list-disc list-inside">
                    <li>To respond to demo requests and inquiries</li>
                    <li>To send product updates if you've opted in</li>
                    <li>To improve our platform and website</li>
                    <li>To maintain security and prevent fraud</li>
                  </ul>
                </section>

                {/* Section 3 */}
                <section className="mb-12">
                  <h2 className="text-2xl font-bold text-text-primary mb-4">
                    Information Sharing
                  </h2>
                  <div className="text-text-secondary space-y-4">
                    <p>
                      <strong className="text-text-primary">We do not sell your personal information.</strong>
                    </p>
                    <p>
                      We may share information with service providers who assist in operating our platform, including Google Cloud and Firebase, under appropriate data protection agreements.
                    </p>
                    <p>
                      We may disclose information if required by law, legal process, or to protect our rights and the safety of our users.
                    </p>
                  </div>
                </section>

                {/* Section 4 */}
                <section className="mb-12">
                  <h2 className="text-2xl font-bold text-text-primary mb-4">
                    Data Security
                  </h2>
                  <ul className="text-text-secondary space-y-3 list-disc list-inside">
                    <li>Data encrypted in transit and at rest</li>
                    <li>Access controls and audit logging</li>
                    <li>Regular security reviews and assessments</li>
                    <li>Multi-tenant architecture with data isolation between boards</li>
                  </ul>
                </section>

                {/* Section 5 */}
                <section className="mb-12">
                  <h2 className="text-2xl font-bold text-text-primary mb-4">
                    Your Rights
                  </h2>
                  <div className="text-text-secondary space-y-4">
                    <p>You have the right to:</p>
                    <ul className="space-y-3 list-disc list-inside">
                      <li>Request access to your personal data</li>
                      <li>Request deletion of your personal data</li>
                      <li>Opt out of marketing communications</li>
                      <li>Contact us with any privacy concerns</li>
                    </ul>
                    <p>
                      To exercise these rights, contact us using the information below.
                    </p>
                  </div>
                </section>

                {/* Section 6 */}
                <section className="mb-12">
                  <h2 className="text-2xl font-bold text-text-primary mb-4">
                    Contact
                  </h2>
                  <div className="text-text-secondary space-y-4">
                    <p>For privacy inquiries:</p>
                    <p>
                      <a
                        href="mailto:john@edaisystems.com"
                        className="text-accent-cyan hover:text-accent-cyan/80 transition-colors"
                      >
                        john@edaisystems.com
                      </a>
                    </p>
                    <p className="text-text-muted">
                      EdAI Systems, Inc. | Honolulu, Hawaii
                    </p>
                  </div>
                </section>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </>
  );
};

export default PrivacyPage;
